import { useSyncExternalStore } from "react";
import { Link } from "./LocalizedLink";
import { ArrowUpRight, ChevronDown, Mail, Phone, Linkedin, Instagram, Facebook, Youtube } from "lucide-react";
import "../v3/css/footer-legal.css";
import { V3_FOOTER, V3_SOCIAL } from "../v3/footer";
import { V3_MENU } from "../v3/menu";
import { v4Label, V4_HIDDEN } from "../v4/labels";
import { v4ProductFooter } from "../v4/menu";
import { softBreaks } from "../v3/Placeholder";
import { SITE } from "../data/seo";
import { Reveal } from "./motion";
import BrandLogo from "./relaunch/BrandLogo";
import { useLanguage } from "../i18n/LanguageContext";
import { useVersion } from "../version/VersionContext";
import { SITE_VERSION } from "../v4/siteVersion";
import navExtra from "../i18n/navExtra";
import FooterV4 from "../v4/FooterV4";

// Fußzeile beider Versionen.
// Version 2: Konzernstruktur (Ablauf und Leistungen, Bestand, Direkt verkaufen, Über Parto, Service).
// Version 1: Struktur der bisherigen Live Seite (Infrastruktur, Solutions, Rollen, Insights, Unternehmen) mit Kontakt.
// Version 3: unter 768 px werden die Spaltengruppen der Fußzeile zum Akkordeon
const FOOT_NARROW = "(max-width: 767px)";
const footMq = () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(FOOT_NARROW) : null);
const footSub = (cb) => { const m = footMq(); if (!m) return () => {}; if (m.addEventListener) { m.addEventListener("change", cb); return () => m.removeEventListener("change", cb); } m.addListener(cb); return () => m.removeListener(cb); };
const useFooterNarrow = () => useSyncExternalStore(footSub, () => !!footMq()?.matches, () => false);

const FooterLegacy = () => {
    const footNarrow = useFooterNarrow();
    const { lang, t } = useLanguage();
    const { version } = useVersion();
    const x = navExtra[lang] || navExtra.de;
    const f = t.footer;
    const r = t.r;
    const rf = r.footer;
    const year = new Date().getFullYear();

    const colTitleCls = "rl-footer-title";
    const linkCls = "rl-flink text-white/65 text-[15px] hover:text-white";
    const legalLinkCls = "rl-flink text-sm text-white/70 hover:text-white";
    const channels = lang === "en"
        ? [{ label: "HubParto", href: "https://hubparto.com/en/" }, { label: "Parto Assets", href: "https://partoassets.com/en/" }, { label: "BuyParto", href: "https://www.buyparto.com/" }]
        : [{ label: "HubParto", href: "https://hubparto.com/" }, { label: "Parto Assets", href: "https://partoassets.com/" }, { label: "BuyParto", href: "https://www.buyparto.com/" }];

    // Spalten je Version: { title, links: [{ label, to }] }
    let cols;
    if (version === "3" || version === "4") {
        // Version 3 und 4: genau die Seiten aus der Navigation, vier Spalten aus src/v3/menu.js.
        // Solutions mit allen drei Gruppen, Produkte und Anlagen mit beiden Gruppen und allen Kategorien (Sprungziele) wie im Menü,
        // Wissen und Unternehmen wie im Menü. Keine zusätzlichen Seiten (die Landingpages stehen nicht im Menü und daher auch nicht hier).
        // Einträge mit „heading“ sind kleine Zwischenüberschriften.
        const k = lang === "en" ? "en" : "de";
        const sec = (id) => V3_MENU.find((m) => m.id === id);
        const grp = (id, gid) => sec(id)?.groups.find((g) => g.id === gid);
        // Lange Komposita bekommen <wbr> an den Wortfugen, damit schmale Spalten nicht überlaufen
        // softBreaks setzt <wbr> nur unter 480 px (siehe src/v3/Placeholder.jsx)
        const brk = softBreaks;
        const links = (g) => (g ? g.items.filter((i) => version !== "4" || !V4_HIDDEN.has(i.to)).map((i) => ({ label: brk(version === "4" ? v4Label(i.to, k, i[k]) : i[k]), to: i.to })) : []);
        const head = (g) => (g ? [{ heading: g[k] }] : []);
        cols = [
            { title: sec("solutions")?.[k], links: ["leistungen", "herausforderung", "rolle"].flatMap((gid) => [...head(grp("solutions", gid)), ...links(grp("solutions", gid))]) },
            { title: sec("produkte")?.[k], links: version === "4" ? v4ProductFooter().map((l) => (l.heading ? l : { ...l, label: brk(l.label) })) : ["komponenten", "maschinen"].flatMap((gid) => [...head(grp("produkte", gid)), ...links(grp("produkte", gid))]) },
            { title: sec("wissen")?.[k], links: links(grp("wissen", "wissen")) },
            { title: sec("unternehmen")?.[k], links: links(grp("unternehmen", "unternehmen")) },
        ].filter((c) => c.title && c.links.length);
    } else if (version === "1") {
        const m = x.v1.menus;
        const pick = (menu) => menu.cols.flatMap((c) => c.items).map(({ label, to }) => ({ label, to }));
        cols = [
            { title: t.nav.infrastructure, links: [{ label: m.infrastructure.title, to: "/infrastructure" }, ...pick(m.infrastructure)] },
            { title: t.nav.solutions, links: [...m.solutions.cols[0].items, ...m.solutions.cols[1].items].map(({ label, to }) => ({ label, to })) },
            { title: t.nav.roles, links: pick(m.roles) },
            { title: t.nav.insights, links: [{ label: m.insights.title, to: "/insights" }, ...pick(m.insights)] },
            { title: t.nav.company, links: pick(m.company).filter((l) => l.to !== "/contact") },
        ];
    } else {
        // Version 2 schlank wie die Navigation (Wunsch 16.10.2026): drei Spalten statt fünf.
        // Leistungen, Branchen, Presse, Standort und die Direkt verkaufen Seiten bleiben erreichbar (Sitemap, Verweise), stehen aber nicht mehr hier.
        const v = x.v2.footer;
        const pickTo = (list, tos) => tos.map((to) => list.find((l) => l.to === to)).filter(Boolean);
        cols = [
            { title: rf.colStock, links: r.nav.stockItems.map(({ label, to }) => ({ label, to })) },
            { title: v.colAbout, links: [...pickTo(v.about, ["/company", "/ecosystem", "/partners", "/brands", "/sustainability"]), ...pickTo(v.service, ["/careers"])] },
            { title: v.colService, links: [...pickTo(v.how, ["/how-it-works", "/references", "/quality"]), ...pickTo(v.service, ["/contact"])] },
        ];
    }

    const v3f = V3_FOOTER[lang === "en" ? "en" : "de"];
    const tagline = (version === "3" || version === "4") ? v3f.claim : version === "1" ? x.v1.footer.tagline : rf.tagline;
    // Version 3 und 4: Anfrage führt auf /kontakt, Telefon klickbar aus SITE.phone
    const ctaTo = (version === "3" || version === "4") ? "/kontakt" : "/contact";
    const ctaLabel = (version === "3" || version === "4") ? v3f.cta : r.nav.cta;
    const telHref = `tel:${SITE.phone.replace(/[^\d+]/g, "")}`;

    // Version 4 (G-05): eigene, übersichtlichere Anordnung mit denselben Inhalten (src/v4/FooterV4.jsx). Versionen 1 bis 3 unverändert.
    if (version === "4") {
        const v3fl = V3_FOOTER[lang === "en" ? "en" : "de"];
        return (
            <FooterV4
                cols={cols} narrow={footNarrow} tagline={tagline} v3f={v3f} ctaTo={ctaTo} ctaLabel={ctaLabel}
                telHref={telHref} site={SITE} channels={channels} channelsTitle={rf.colChannels}
                social={V3_SOCIAL} socialTitle={v3fl.social} socialNote={v3fl.socialMissing}
                year={year} rights={f.rights} siteVersion={SITE_VERSION} colTitleCls={colTitleCls}
                legal={[
                    { to: "/impressum", label: f.imprint, testId: "footer-imprint-link" },
                    { to: "/datenschutz", label: f.privacy, testId: "footer-privacy-link" },
                    { to: "/llm-info", label: v3f.llm, testId: "footer-llm-info-link" },
                ]}
            />
        );
    }

    // Linkliste einer Spalte (Einträge mit heading sind kleine Zwischenüberschriften, nur Version 3)
    const listOf = (c) => (
        <ul className={`grid gap-3 list-none m-0 p-0 ${(version === "3" || version === "4") ? "v3-flist" : ""}`}>
            {c.links.map((l, i) => (l.heading
                ? <li key={`h-${l.heading}`} className="pt-3 text-[11px] font-bold uppercase tracking-[.14em] text-white/65">{l.heading}</li>
                : <li key={l.to}><Link to={l.to} data-testid={c.testPrefix ? `${c.testPrefix}-${i}` : undefined} className={(version === "3" || version === "4") ? `${linkCls} v3-ftap [overflow-wrap:normal] [word-break:normal] [hyphens:manual]` : linkCls}>{l.label}</Link></li>
            ))}
        </ul>
    );

    return (
        <footer className="site-footer on-deep relative overflow-hidden" data-testid="main-footer" data-footer-version={version}>
            <div className="site-footer__content relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-20 pb-6 md:pb-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8">
                    <div className="lg:col-span-3">
                        <Link to="/" className="inline-flex" data-testid="footer-logo-link" aria-label="Parto Solution">
                            <img src="/brand/parto-logo-negativ.svg" alt="Parto Solution" className="h-12 w-auto" width={(version === "3" || version === "4") ? 192 : undefined} height={(version === "3" || version === "4") ? 48 : undefined} loading={(version === "3" || version === "4") ? "lazy" : undefined} decoding={(version === "3" || version === "4") ? "async" : undefined} />
                        </Link>
                        <p className="text-white/75 text-sm mt-6 max-w-xs leading-relaxed m-0">{tagline}</p>
                        {(version === "3" || version === "4") && <p lang="en" data-testid="footer-v3-claim-en" className="text-white/70 text-xs mt-3 max-w-xs leading-relaxed m-0 italic">{v3f.claimEn}</p>}
                        <Link to={ctaTo} data-testid="footer-cta-link" className="rl-btn rl-btn-primary mt-8">
                            {ctaLabel} <ArrowUpRight size={15} className="arrow" />
                        </Link>

                        {(version === "3" || version === "4") && (
                            <ul className="v3-flist mt-8 grid gap-3 list-none m-0 p-0 text-[15px]" data-testid="footer-v3-contact">
                                <li><a href={telHref} data-testid="footer-phone-link" className={`${linkCls} v3-ftap inline-flex items-center gap-2`}><Phone size={14} aria-hidden="true" /> {SITE.phone}</a></li>
                                <li><a href={`mailto:${SITE.email}`} data-testid="footer-mail-link" className={`${linkCls} v3-ftap inline-flex items-center gap-2`}><Mail size={14} aria-hidden="true" /> {SITE.email}</a></li>
                            </ul>
                        )}

                        {version === "1" && (
                            <div className="mt-10">
                                <h2 className={colTitleCls}>{x.v1.footer.colContact}</h2>
                                <ul className="grid gap-3 list-none m-0 p-0 text-[15px]">
                                    <li><a href="mailto:info@parto-solution.com" data-testid="footer-mail-link" className={`${linkCls} inline-flex items-center gap-2`}><Mail size={14} aria-hidden="true" /> info@parto-solution.com</a></li>
                                    <li><a href="tel:+4959719629031" data-testid="footer-phone-link" className={`${linkCls} inline-flex items-center gap-2`}><Phone size={14} aria-hidden="true" /> +49 5971 9629031</a></li>
                                    <li><Link to="/contact" data-testid="footer-contact-link" className={linkCls}>{x.v1.footer.contactLink}</Link></li>
                                </ul>
                            </div>
                        )}

                        {version !== "3" && version !== "4" && (
                        <div className="mt-10">
                            <h2 className={colTitleCls}>{version === "1" ? x.v1.footer.colChannels : rf.colChannels}</h2>
                            <ul className="flex flex-wrap lg:grid gap-x-6 gap-y-3 list-none m-0 p-0">
                                {channels.map((c, i) => (
                                    <li key={c.label}>
                                        <a href={c.href} target="_blank" rel="noreferrer" data-testid={`footer-eco-link-${i}`} aria-label={c.label} className="group inline-flex items-center gap-2 opacity-85 hover:opacity-100 transition-opacity">
                                            <BrandLogo name={c.label} tone="dark" className={c.label === "BuyParto" ? "h-8" : "h-6"} /> <ArrowUpRight size={13} className="text-white/55 group-hover:text-white" />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        )}
                    </div>

                    {/* Version 3: unter 768 px Akkordeon (Spaltengruppen zum Aufklappen), sonst Spalten; vier Spalten erst ab 1280 px */}
                    {(version === "3" || version === "4") && footNarrow ? (
                        <div className="lg:col-span-9 border-t border-white/15" data-testid="footer-v3-accordion">
                            {cols.map((c, ci) => (
                                <details key={c.title} className="v3-facc border-b border-white/15" data-testid={`footer-col-${ci}`}>
                                    <summary><h2 className={colTitleCls}>{c.title}</h2><ChevronDown size={20} strokeWidth={1.75} className="text-white/70" aria-hidden="true" /></summary>
                                    {listOf(c)}
                                </details>
                            ))}
                        </div>
                    ) : (
                    <div className={`lg:col-span-9 grid ${(version === "3" || version === "4") ? "grid-cols-1 min-[420px]:grid-cols-2" : "grid-cols-2"} ${(version === "3" || version === "4") ? "xl:grid-cols-4" : `${cols.length === 4 ? "md:grid-cols-4" : "md:grid-cols-3"} ${cols.length === 5 ? "xl:grid-cols-5" : ""}`} gap-x-6 gap-y-10`}>
                        {cols.map((c, ci) => (
                            <div key={c.title} data-testid={`footer-col-${ci}`}>
                                <h2 className={colTitleCls}>{c.title}</h2>
                                {listOf(c)}
                            </div>
                        ))}
                    </div>
                    )}
                </div>


                {/* Version 3: Kanäle und Social Media über die ganze Breite verteilt */}
                {(version === "3" || version === "4") && (
                    <div className="mt-14 pt-10 border-t border-white/15 flex flex-col md:flex-row md:items-start md:justify-between gap-10" data-testid="footer-v3-row">
                        <div>
                            <h2 className={colTitleCls}>{rf.colChannels}</h2>
                            <ul className="flex flex-wrap items-center gap-x-10 gap-y-4 list-none m-0 p-0">
                                {channels.map((c, i) => (
                                    <li key={c.label}>
                                        <a href={c.href} target="_blank" rel="noreferrer" data-testid={`footer-eco-link-${i}`} aria-label={c.label} className="group v3-ftap inline-flex items-center gap-2 opacity-85 hover:opacity-100 transition-opacity">
                                            <BrandLogo name={c.label} tone="dark" className={c.label === "BuyParto" ? "h-8" : "h-6"} /> <ArrowUpRight size={13} className="text-white/55 group-hover:text-white" />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        {/* Social Media (Adressen folgen, bis dahin Platzhalter) */}
                            <div data-testid="footer-v3-social">
                                <h2 className={colTitleCls}>{V3_FOOTER[lang === "en" ? "en" : "de"].social}</h2>
                                <ul className="flex flex-wrap gap-2 m-0 p-0 list-none">
                                    {V3_SOCIAL.map((sc) => {
                                        const Icon = { linkedin: Linkedin, instagram: Instagram, facebook: Facebook, youtube: Youtube }[sc.id];
                                        const missing = !sc.href;
                                        const note = V3_FOOTER[lang === "en" ? "en" : "de"].socialMissing;
                                        // Adresse fehlt: nicht klickbarer Platzhalter (span, deaktivierter Link mit Hinweis), Aussehen wie bisher
                                        return (
                                            <li key={sc.id}>
                                                {missing ? (
                                                    <span role="link" aria-disabled="true" aria-label={`${sc.label} ${note}`} title={`${sc.label} ${note}`}
                                                        data-testid={`footer-social-${sc.id}`} className="v3-social is-missing">
                                                        <Icon size={18} aria-hidden="true" />
                                                    </span>
                                                ) : (
                                                    <a href={sc.href} target="_blank" rel="noreferrer" aria-label={sc.label} title={sc.label}
                                                        data-testid={`footer-social-${sc.id}`} className="v3-social">
                                                        <Icon size={18} aria-hidden="true" />
                                                    </a>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                    </div>
                )}
                <div className="border-t border-white/15 mt-14 pt-5 flex flex-col md:flex-row md:items-center justify-between gap-3 text-sm text-white/70">
                    <p className="m-0">© Parto Solution GmbH {year}. {f.rights}{version === "4" && <span className="v4-site-version" data-testid="footer-site-version"> · v{SITE_VERSION}</span>}</p>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                        <Link to="/impressum" data-testid="footer-imprint-link" className={`${legalLinkCls} ${(version === "3" || version === "4") ? "v3-ftap" : ""}`}>{f.imprint}</Link>
                        <Link to="/datenschutz" data-testid="footer-privacy-link" className={`${legalLinkCls} ${(version === "3" || version === "4") ? "v3-ftap" : ""}`}>{f.privacy}</Link>
                        <Link to="/llm-info" data-testid="footer-llm-info-link" className={`${legalLinkCls} ${(version === "3" || version === "4") ? "v3-ftap" : ""}`}>{(version === "3" || version === "4") ? v3f.llm : f.llmInfo}</Link>
                    </div>
                </div>
            </div>

            {/* Großer PARTO Schriftzug, sehr dezent hinter dem Inhalt, sitzt auf der Unterkante der Seite und wird dort angeschnitten */}
            <div className="absolute inset-x-0 bottom-0 z-0 overflow-hidden select-none pointer-events-none h-[clamp(3.5rem,14vw,16rem)]" aria-hidden="true">
                <Reveal y={0} viewport={{ once: true, margin: "0px" }} className="absolute inset-x-0 top-0 flex justify-center">
                    <span className="block font-black text-[clamp(5rem,21vw,25rem)] leading-[0.74] tracking-[-0.04em] text-white" style={{ opacity: 0.02 }}>PARTO</span>
                </Reveal>
            </div>
        </footer>
    );
};

export default FooterLegacy;
